import { Box, Button, Container, Typography } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import React from 'react';
import { isChunkLoadError, reloadOnceForStaleChunk } from '@/lib/chunkReload';

interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Fängt unerwartete Render-Fehler in der React-Komponenten-Hierarchie ab,
 * damit nicht die ganze App weiß wird. Zeigt eine freundliche Fehlerseite
 * mit Reload- und „Zur Startseite"-Button.
 *
 * Eingesetzt in src/main.tsx als äußerster Wrapper unter ThemeProvider.
 */
export class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Sicherheitsnetz: Erreicht ein Stale-Chunk-Fehler doch die Boundary
    // (statt vom lazyWithReload-Wrapper abgefangen zu werden), einmal hart
    // neu laden. Der sessionStorage-Debounce verhindert Reload-Loops — bei
    // wiederholtem Fehler bleibt die normale Fehlerseite stehen.
    if (isChunkLoadError(error) && reloadOnceForStaleChunk()) return;
    // In Produktion an Sentry/Logflare/etc. weiterleiten — vorerst nur Console.
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', error, info);
  }

  handleReload = () => {
    window.location.reload();
  };

  handleHome = () => {
    window.location.href = '/';
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <Container maxWidth="sm" sx={{ py: { xs: 6, md: 10 }, textAlign: 'center' }}>
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 64,
            height: 64,
            borderRadius: '50%',
            bgcolor: 'rgba(220, 38, 38, 0.08)',
            color: '#dc2626',
            fontSize: 32,
            fontWeight: 800,
            mb: 3,
          }}
        >
          !
        </Box>
        <Typography variant="h1" sx={{ fontSize: { xs: '1.5rem', md: '2rem' }, fontWeight: 700, mb: 1.5 }}>
          Etwas ist schiefgelaufen
        </Typography>
        <Typography sx={{ color: 'text.secondary', mb: 4, lineHeight: 1.6 }}>
          Wir konnten diese Seite gerade nicht laden. Versuche es mit einem Reload — oder geh zurück zur Startseite.
          Wenn das öfter passiert, schreib uns kurz, was du gemacht hast.
        </Typography>
        {this.state.error?.message ? (
          <Typography
            sx={{
              fontFamily: 'ui-monospace, Menlo, Consolas, monospace',
              fontSize: '0.78rem',
              color: '#94a3b8',
              bgcolor: '#FAF6F0',
              border: '1px solid rgba(15,23,42,0.08)',
              borderRadius: 1.5,
              p: 1.5,
              mb: 4,
              wordBreak: 'break-word',
            }}
          >
            {this.state.error.message}
          </Typography>
        ) : null}
        <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            size="large"
            startIcon={<RefreshIcon />}
            onClick={this.handleReload}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            Seite neu laden
          </Button>
          <Button
            variant="outlined"
            size="large"
            startIcon={<HomeOutlinedIcon />}
            onClick={this.handleHome}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Zur Startseite
          </Button>
        </Box>
      </Container>
    );
  }
}
